import { ButtonIcon } from '@/components';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '@/components';

export interface MoreDropdownAction<T = string> {
  key: T;
  label: string;
}

export interface MoreDropdownProps<T = string> {
  label: string;
  actions: MoreDropdownAction<T>[];
  onAction: (key: T) => void;
  width?: number;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * @component MoreDropdown
 * @description A dropdown component with a more options button trigger.
 *
 * @props
 *   label (string, required) - Accessibility label for the trigger button.
 *   actions (MoreDropdownAction[], required) - Array of actions with key and label.
 *   onAction (function, required) - Callback function called when an action is selected.
 *   width (number, optional) - Width of the dropdown content in pixels. Defaults to 180.
 *   disabled (boolean, optional) - Whether the dropdown is disabled. Defaults to false.
 *   ariaLabel (string, optional) - Additional aria-label for accessibility.
 */
export function MoreDropdown<T = string>({
  label,
  actions,
  onAction,
  width = 180,
  disabled = false,
  ariaLabel,
}: MoreDropdownProps<T>) {
  return (
    <Dropdown>
      <DropdownTrigger
        aria-label={ariaLabel || label}
        disabled={disabled}
      >
        <ButtonIcon
          icon="more"
          ariaLabel={label}
          loading={false}
          disabled={disabled}
          onClick={() => {}}
        />
      </DropdownTrigger>
      <DropdownContent width={width}>
        {actions.map((action) => (
          <DropdownItem 
            key={String(action.key)} 
            onSelect={() => onAction(action.key)}
          >
            {action.label}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
}
